export interface CustomSkillSuggestion {
  /** Names the suggestion in analytics. */
  kind: string
  /** Catalog provider ids, as `getUsableIntegrationProviderIds` returns them, that qualify a user for it. */
  providers: ReadonlyArray<string>
  /** The composer opens holding this, unsent. `{tool}` is the display name of the first matching provider. */
  prompt: string
}

const PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  'google-gmail': 'Gmail',
  ashby: 'Ashby',
  greenhouse: 'Greenhouse',
  gem: 'Gem',
  hubspot: 'HubSpot',
  slack: 'Slack',
  linear: 'Linear',
  asana: 'Asana',
  atlassian: 'Jira',
}

/**
 * In priority order; the first one the user has a connection for wins. Each is a recurring sweep
 * that drafts something for the user to approve, so it has a good chance of producing staged
 * actions. None summarizes, since the built-in Daily Brief already does that each weekday morning.
 */
export const CUSTOM_SKILL_SUGGESTIONS: ReadonlyArray<CustomSkillSuggestion> = [
  {
    kind: 'daily email replies',
    providers: ['google-gmail'],
    prompt:
      'Create a custom skill called "Daily email replies" that runs every weekday morning: go through the {tool} emails since the last run that are still waiting on a reply from me, and draft a reply to each one in my voice so I can approve or edit it. Skip newsletters, notifications, and threads I have already answered.',
  },
  {
    kind: 'daily applicant review',
    providers: ['ashby', 'greenhouse', 'gem'],
    prompt:
      'Create a custom skill called "Daily applicant review" that runs every weekday morning: go through the candidates added in {tool} since the last run and give each a one-line fit assessment against the role. For the strong fits, draft a short note to the hiring manager recommending them; for the clear poor fits, draft a polite decline in line with how our team replies to applicants, so I can approve or edit each one.',
  },
  {
    kind: 'daily pipeline review',
    providers: ['hubspot'],
    prompt:
      'Create a custom skill called "Daily pipeline review" that runs every weekday morning: find the deals in {tool} that have gone quiet or stalled in a stage, draft the follow-up email for each so I can approve or edit it, and summarize the rest of the pipeline.',
  },
  {
    kind: 'daily slack replies',
    providers: ['slack'],
    prompt:
      'Create a custom skill called "Daily Slack replies" that runs every weekday morning: find the {tool} mentions and direct messages since the last run that are still waiting on a reply from me, and draft a reply to each one in my voice so I can approve or edit it. Skip anything I have already answered or that only needs a reaction.',
  },
  {
    kind: 'daily issue triage',
    providers: ['linear', 'asana', 'atlassian'],
    prompt:
      'Create a custom skill called "Daily issue triage" that runs every weekday morning: review the issues in {tool} created or updated since the last run that are assigned to me, mention me, or sit untriaged in my team\'s queue. Where one needs a reply, a priority, an owner, or a status change, draft that comment or update so I can approve or edit it. For issues with no movement in a week, draft a short check-in comment to the assignee.',
  },
]

/** When no connected integration suggests a specific workflow, the chat works it out from how the user works. */
export const GENERIC_CUSTOM_SKILL_SUGGESTION: CustomSkillSuggestion = {
  kind: 'from how you work',
  providers: [],
  prompt:
    'Create a custom skill that automates a workflow for me. Start from how I actually work: the apps and tools I use, the meetings I take, and the tasks I repeat. Propose the one skill that would save me the most time and that my existing skills do not already cover, describe what it does step by step and when it should run, and create it once I confirm.',
}

export interface ResolvedCustomSkillSuggestion {
  kind: string
  prompt: string
}

/** The highest-priority suggestion the user's connected integrations qualify for, else the generic one. */
export function customSkillSuggestionFor(connectedProviderIds: ReadonlySet<string>): ResolvedCustomSkillSuggestion {
  for (const suggestion of CUSTOM_SKILL_SUGGESTIONS) {
    const tool = suggestion.providers.find((id) => connectedProviderIds.has(id))
    if (tool) {
      return {
        kind: suggestion.kind,
        prompt: suggestion.prompt.replace('{tool}', PROVIDER_DISPLAY_NAMES[tool] ?? tool),
      }
    }
  }
  return { kind: GENERIC_CUSTOM_SKILL_SUGGESTION.kind, prompt: GENERIC_CUSTOM_SKILL_SUGGESTION.prompt }
}
